/**
 * Single source of truth for the site's URLs.
 *
 * Plain JS (not .ts) on purpose: `server.js` runs under plain Node and cannot
 * import a TypeScript module, while Vite happily bundles this into the client.
 * Keeping one table means the app, the server's 404 handling, and the sitemap
 * can never drift apart.
 *
 * @typedef {Object} RouteDef
 * @property {string}  path        URL path, no trailing slash (except '/').
 * @property {string}  view        Key into the view map in App.tsx.
 * @property {string}  title       <title> for this URL.
 * @property {string}  description <meta name="description"> for this URL.
 * @property {string}  [heading]   <h1> rendered above the view. Omit when the
 *                                 view already renders its own <h1>.
 * @property {number}  priority    Sitemap priority.
 */

/** @type {RouteDef[]} */
export const ROUTES = [
  {
    path: '/',
    view: 'HOME',
    title: 'Unix Timestamp Converter - Epoch to Date & Date to Epoch | EpochTimer',
    description:
      'Convert Unix timestamps to human-readable dates and back, in seconds or milliseconds. Includes duration conversion, quick epoch references, and ready-to-paste code snippets.',
    heading: 'Unix Timestamp Converter',
    priority: 1.0,
  },
  {
    path: '/specialized',
    view: 'SPECIALIZED',
    title: 'Specialized Timestamp Converters - Windows, Mac & Excel | EpochTimer',
    description:
      'Convert between Unix epoch and specialized time formats such as Windows FILETIME, Mac absolute time, and Excel serial dates.',
    heading: 'Specialized Timestamp Converters',
    priority: 0.8,
  },
  {
    path: '/batch',
    view: 'BATCH',
    title: 'Batch Timestamp Converter - Convert Many Epochs at Once | EpochTimer',
    description:
      'Paste a list of Unix timestamps and convert all of them to readable dates in one pass. Runs entirely in your browser, so nothing is uploaded.',
    heading: 'Batch Timestamp Converter',
    priority: 0.8,
  },
  {
    path: '/countdown',
    view: 'COUNTDOWN',
    title: 'Unix Timestamp Countdown Timer | EpochTimer',
    description:
      'Count down to any Unix timestamp or future date and watch the remaining days, hours, minutes, and seconds update live.',
    heading: 'Countdown to a Unix Timestamp',
    priority: 0.8,
  },
  {
    path: '/time-zones',
    view: 'TIMEZONES',
    title: 'Time Zone Converter - Compare Epoch Across Zones | EpochTimer',
    description:
      'See what a Unix timestamp looks like across world time zones, including UTC offsets and daylight saving status.',
    heading: 'Time Zone Converter',
    priority: 0.8,
  },
  {
    path: '/epoch-lists',
    view: 'LISTS',
    title: 'Epoch Reference Lists - Timestamps by Year, Month & Week | EpochTimer',
    description:
      'Browse reference tables of Unix timestamps for the start and end of years, months, and weeks. Handy for query boundaries and test fixtures.',
    heading: 'Epoch Reference Lists',
    priority: 0.8,
  },
  {
    path: '/date-tools',
    view: 'DATE_TOOLS',
    title: 'Date Calculator - Add, Subtract & Diff Dates | EpochTimer',
    description:
      'Add or subtract days from a date, measure the distance between two dates, and read the result as both a calendar date and a Unix timestamp.',
    heading: 'Date Tools',
    priority: 0.8,
  },
  {
    path: '/what-is-epoch-time',
    view: 'REFERENCE',
    title: 'What Is Epoch Time? Unix Time Explained | EpochTimer',
    description:
      'Epoch time is the number of seconds since 1 January 1970 UTC, ignoring leap seconds. What it is, why 1970, how seconds differ from milliseconds, and the 2038 problem.',
    heading: 'What Is Epoch Time?',
    priority: 0.9,
    body: [
      'Epoch time is a single number: the count of seconds that have elapsed since 00:00:00 UTC on 1 January 1970, not counting leap seconds. That instant is called the Unix epoch, and the number is variously called epoch time, Unix time, POSIX time or a Unix timestamp — they all mean the same thing.',
      'Because it is one integer in one time zone, it is unambiguous. "1 March 2026, 14:00" needs a time zone and a date format before two systems can agree on it; 1772373600 does not.',
    ],
    sections: [
      {
        h2: 'Why 1 January 1970?',
        paras: [
          'It is an arbitrary but convenient choice. Unix was being developed at Bell Labs in the late 1960s, and the team needed a recent, round starting point that fit in the machine word sizes of the day. An earlier version counted sixtieths of a second from 1971, which overflowed in about two and a half years; moving to whole seconds from the start of 1970 gave a range measured in decades. It stuck, and is now baked into essentially every operating system, database and programming language.',
        ],
      },
      {
        h2: 'Seconds or milliseconds?',
        paras: [
          'This is the single most common source of confusion, because both are called "the timestamp".',
        ],
        list: [
          'Ten digits is seconds — the Unix convention, used by most databases, Linux tools and APIs.',
          'Thirteen digits is milliseconds — the JavaScript convention, returned by Date.now().',
          'Sixteen digits is microseconds, and nineteen is nanoseconds — used by Go, some Python APIs and observability tooling.',
          'A quick sanity check: a seconds timestamp for any date in the 2020s starts with 1 and has ten digits. If your date lands in 1970, you passed seconds to something expecting milliseconds. If it lands tens of thousands of years in the future, you did the reverse.',
        ],
      },
      {
        h2: 'What epoch time does not include',
        paras: [
          'Unix time deliberately ignores leap seconds. Every day is treated as exactly 86,400 seconds, so when a leap second is inserted, Unix time repeats or stretches a second rather than counting it. This keeps the arithmetic simple — any date can be converted by dividing and multiplying — at the cost of being a few dozen seconds away from strict atomic time.',
          'It also carries no time zone and no daylight saving information. Epoch time is always UTC. Local time is something you apply when you display the value, never something stored inside it.',
        ],
      },
      {
        h2: 'The year 2038 problem',
        paras: [
          'A signed 32-bit integer runs out at 2,147,483,647 seconds, which is 03:14:07 UTC on 19 January 2038. Systems still storing time in a signed 32-bit field will wrap around to December 1901 at that moment. Modern 64-bit systems are unaffected — a signed 64-bit count of seconds lasts about 292 billion years — but embedded devices, old file formats and some database columns still need auditing.',
        ],
      },
      {
        h2: 'Where you will meet it',
        list: [
          'Log files and observability tooling, where a single sortable integer beats a formatted date.',
          'JWTs and session tokens, whose iat and exp claims are epoch seconds.',
          'HTTP caching, file system mtimes, and Git commit timestamps.',
          'Database range queries, where comparing integers is faster and less error-prone than comparing formatted dates.',
        ],
      },
    ],
    faq: [
      { q: 'What is epoch time?', a: 'Epoch time is the number of seconds elapsed since 00:00:00 UTC on 1 January 1970, not counting leap seconds. It is also called Unix time, POSIX time or a Unix timestamp, and it is always expressed in UTC.' },
      { q: 'Why does epoch time start in 1970?', a: 'It was an arbitrary but convenient choice made while Unix was being developed at Bell Labs: a recent, round starting point that fit the machine word sizes of the day and gave a range measured in decades rather than years.' },
      { q: 'Is epoch time in seconds or milliseconds?', a: 'Unix epoch time is in seconds and has ten digits for current dates. JavaScript uses milliseconds, which is thirteen digits. Sixteen digits is microseconds and nineteen is nanoseconds.' },
      { q: 'Does epoch time include leap seconds?', a: 'No. Unix time treats every day as exactly 86,400 seconds, so leap seconds are repeated or stretched rather than counted. This keeps date arithmetic simple but leaves it a few dozen seconds off strict atomic time.' },
      { q: 'What is the year 2038 problem?', a: 'A signed 32-bit timestamp overflows at 03:14:07 UTC on 19 January 2038 and wraps to December 1901. Systems using 64-bit timestamps are unaffected.' },
    ],
  },
  {
    path: '/posix-time',
    view: 'REFERENCE',
    title: 'POSIX Time vs Unix Time vs Epoch Time | EpochTimer',
    description:
      'POSIX time, Unix time and epoch time are the same number defined by different documents. What the POSIX standard actually specifies, and where the definitions diverge.',
    heading: 'POSIX Time, Unix Time and Epoch Time',
    priority: 0.9,
    body: [
      'POSIX time, Unix time, epoch time and Unix timestamp all name the same value: seconds elapsed since 1 January 1970 UTC, with every day counted as exactly 86,400 seconds. The different names come from different documents describing it, not from different numbers.',
    ],
    sections: [
      {
        h2: 'What the POSIX standard actually says',
        paras: [
          'POSIX (IEEE Std 1003.1) defines "seconds since the Epoch" with an explicit formula that converts a broken-down UTC date into a single number. The formula multiplies days by 86,400 and adds hours, minutes and seconds. Crucially, it has no term for leap seconds — which is why POSIX time is not a true count of elapsed physical seconds, and why it repeats a value whenever a leap second is inserted.',
          'The standard also fixes the epoch itself at 1 January 1970 00:00:00 UTC, and specifies the type as time_t. It deliberately does not fix the width of time_t, which is the reason the 2038 rollover is an implementation problem rather than a standards problem.',
        ],
      },
      {
        h2: 'Where the terms differ in practice',
        list: [
          'POSIX time — the formal name, used when the exact standard definition matters, such as in specifications or when discussing leap-second behaviour.',
          'Unix time — the everyday engineering term for the same number.',
          'Epoch time — the same again, emphasising that it is measured from a fixed origin.',
          'Unix timestamp — usually refers to a specific value rather than the concept.',
          'UTC — a time standard, not a number. A timestamp is expressed in UTC, but UTC itself is not a timestamp.',
        ],
      },
      {
        h2: 'Related but genuinely different scales',
        list: [
          'TAI — International Atomic Time, which does count every physical second and is currently 37 seconds ahead of UTC. Unlike POSIX time, TAI never repeats a value.',
          'GPS time — counts from 6 January 1980 and ignores leap seconds, so it drifts from UTC by a fixed and growing offset (18 seconds as of 2026).',
          'Windows FILETIME — 100-nanosecond intervals since 1 January 1601 UTC. Converting to Unix time means dividing by 10,000,000 and subtracting 11,644,473,600.',
          'Mac absolute time — seconds since 1 January 2001 UTC. Add 978,307,200 to get Unix time.',
          'Excel serial dates — days since 0 January 1900, with a deliberate bug treating 1900 as a leap year.',
        ],
      },
      {
        h2: 'Why the leap-second detail matters',
        paras: [
          'If you are measuring durations across a leap second — in scientific instrumentation, finance, or distributed consensus — POSIX time will under-count by one second, and can hand you the same timestamp twice. For almost everything else, the simplicity is worth far more than the accuracy, which is why the entire industry runs on it.',
        ],
      },
    ],
    faq: [
      { q: 'Is POSIX time the same as Unix time?', a: 'Yes. POSIX time, Unix time, epoch time and Unix timestamp all refer to the number of seconds since 1 January 1970 UTC, with every day counted as exactly 86,400 seconds. The names come from different documents, not different values.' },
      { q: 'Does POSIX time count leap seconds?', a: 'No. The POSIX formula for seconds since the Epoch has no leap-second term, so a leap second causes the same value to repeat rather than the count to advance.' },
      { q: 'What is the difference between UTC and a Unix timestamp?', a: 'UTC is a time standard; a Unix timestamp is a number. Timestamps are expressed in UTC, but UTC itself is not a number and carries no epoch.' },
      { q: 'How does TAI differ from POSIX time?', a: 'TAI counts every physical second and is currently 37 seconds ahead of UTC, never repeating a value. POSIX time skips leap seconds, so it stays aligned to civil time but is not a true elapsed-second count.' },
    ],
  },
  {
    path: '/get-current-epoch-time',
    view: 'REFERENCE',
    title: 'How to Get the Current Epoch Time in Any Language | EpochTimer',
    description:
      'Get the current Unix timestamp in Python, JavaScript, Java, Go, PHP, C#, Ruby, Rust, SQL, bash and Excel — with the seconds-versus-milliseconds trap called out for each.',
    heading: 'How to Get the Current Epoch Time',
    priority: 0.9,
    body: [
      'Every language can produce the current Unix timestamp in a line. The trap is that they do not agree on the unit: some return seconds, some return milliseconds, and a few return floats. The snippets below are grouped by what they actually return.',
    ],
    sections: [
      {
        h2: 'Languages that return seconds',
        list: [
          'Python: int(time.time()) — time.time() itself returns a float with sub-second precision.',
          'PHP: time()',
          'Ruby: Time.now.to_i',
          'Go: time.Now().Unix()',
          'Rust: SystemTime::now().duration_since(UNIX_EPOCH).unwrap().as_secs()',
          'bash: date +%s',
          'PostgreSQL: SELECT EXTRACT(EPOCH FROM NOW())::bigint',
          'MySQL: SELECT UNIX_TIMESTAMP()',
        ],
      },
      {
        h2: 'Languages that return milliseconds',
        list: [
          'JavaScript and TypeScript: Date.now() — divide by 1000 and floor for seconds.',
          'Java: System.currentTimeMillis(), or Instant.now().getEpochSecond() for seconds.',
          'Kotlin: System.currentTimeMillis()',
          'C#: DateTimeOffset.UtcNow.ToUnixTimeMilliseconds(), or ToUnixTimeSeconds() for seconds.',
          'Swift: Date().timeIntervalSince1970 returns a Double in seconds, so multiply for milliseconds.',
        ],
      },
      {
        h2: 'Converting between the two',
        paras: [
          'Going from milliseconds to seconds, always floor rather than round: rounding can push a timestamp into the next second and break comparisons that assume monotonic ordering. Going the other way, multiply by exactly 1000 rather than reformatting through a date string, which risks a time-zone conversion you did not intend.',
        ],
      },
      {
        h2: 'Excel and spreadsheets',
        paras: [
          'Spreadsheets store dates as days since 1900, not seconds since 1970. To turn a Unix timestamp in cell A1 into a readable date: =(A1/86400)+DATE(1970,1,1), then format the result as a date. To go the other way: =(A1-DATE(1970,1,1))*86400.',
        ],
      },
      {
        h2: 'A note on clocks',
        paras: [
          'All of the above read the system wall clock, which can jump backwards when NTP corrects it or when a user changes the time. For measuring how long something took, use your platform\'s monotonic clock instead — time.monotonic() in Python, performance.now() in JavaScript, System.nanoTime() in Java. Use epoch time to record when something happened, not to measure how long it took.',
        ],
      },
    ],
    faq: [
      { q: 'How do I get the current epoch time in Python?', a: 'Use int(time.time()) for whole seconds. time.time() on its own returns a float with sub-second precision.' },
      { q: 'How do I get the current Unix timestamp in JavaScript?', a: 'Date.now() returns milliseconds. For seconds, use Math.floor(Date.now() / 1000).' },
      { q: 'How do I get epoch time in bash?', a: 'Run date +%s for seconds, or date +%s%3N on GNU coreutils for milliseconds.' },
      { q: 'How do I convert a Unix timestamp in Excel?', a: 'With the timestamp in A1, use =(A1/86400)+DATE(1970,1,1) and format the cell as a date. To convert a date back, use =(A1-DATE(1970,1,1))*86400.' },
      { q: 'Should I use epoch time to measure elapsed time?', a: 'No. The system wall clock can jump when NTP corrects it. Use a monotonic clock — time.monotonic(), performance.now() or System.nanoTime() — for durations, and epoch time for recording when an event happened.' },
    ],
  },
  {
    path: '/year-2038-problem',
    view: 'REFERENCE',
    title: 'The Year 2038 Problem Explained: Who Is Affected and How to Fix It | EpochTimer',
    description:
      'At 03:14:07 UTC on 19 January 2038, signed 32-bit Unix timestamps overflow and wrap to 1901. What breaks, what is already fixed, how to audit your systems, and how to migrate.',
    heading: 'The Year 2038 Problem',
    priority: 0.8,
    body: [
      'A signed 32-bit integer tops out at 2,147,483,647. Counted as seconds from the Unix epoch, that is 03:14:07 UTC on Tuesday 19 January 2038. One second later, a system still storing time in a signed 32-bit field wraps around to -2,147,483,648 — which decodes to 20:45:52 UTC on 13 December 1901.',
      'This is the same class of defect as the year 2000 problem, but harder to spot: the limit lives in a binary storage format, not in how dates were typed. Nothing looks wrong until the rollover, and then everything that compares, sorts or schedules by time is wrong at once.',
    ],
    sections: [
      {
        h2: 'What actually breaks',
        paras: [
          'The failure is not cosmetic. Certificates appear to have expired a century ago, sessions and tokens become invalid, scheduled jobs either fire endlessly or never, file copies decide the destination is older than the source, and databases sort 2038 events before 1902 ones. Anything that computes "how long until X" gets a negative number.',
          'Failures also arrive early. Systems that work with future dates — a 20-year mortgage schedule, a 15-year certificate, a long-lived embedded warranty clock — hit the ceiling as soon as the future date they handle crosses 2038, not in 2038 itself.',
        ],
      },
      {
        h2: 'What is already fixed',
        list: [
          'Modern 64-bit operating systems: time_t is 64-bit on 64-bit Linux, macOS, Windows and the BSDs. A signed 64-bit count of seconds lasts about 292 billion years.',
          'Linux on 32-bit hardware: kernel 5.6 (2020) completed 64-bit time syscalls, and glibc 2.34 lets 32-bit programs opt in with _TIME_BITS=64. Debian 13 rebuilt its 32-bit ARM ports this way.',
          'ext4 file systems with 256-byte inodes store timestamps good until the year 2446; XFS gained a similar extension ("bigtime") in kernel 5.10.',
          'JavaScript, Python, Java and most managed languages: they already use 64-bit or arbitrary-precision numbers for time and are unaffected at the language level.',
        ],
      },
      {
        h2: 'Where it still lurks',
        list: [
          'Embedded and IoT devices: routers, cameras, industrial controllers and vehicle ECUs on 32-bit chips with no update path. Devices sold today with 15-year lifetimes will still be running in 2038.',
          'MySQL and MariaDB TIMESTAMP columns: the type is defined as 32-bit epoch seconds and ends at exactly 2038-01-19 03:14:07 UTC. DATETIME reaches the year 9999 and is the standard migration target.',
          'Old file formats and protocols that serialise a 32-bit timestamp: classic cpio and ar archives, some NFSv3 deployments, legacy financial message formats.',
          'Application code that casts time to int or stores epoch seconds in a 32-bit column, even on a 64-bit platform — the platform being fixed does not fix the schema.',
        ],
      },
      {
        h2: 'How to audit and migrate',
        paras: [
          'The practical test is simple: set a test system\u2019s clock past 19 January 2038, or feed the code a timestamp above 2,147,483,647, and watch what happens. On the storage side, search schemas for 32-bit integer columns holding epoch seconds and for MySQL TIMESTAMP columns; migrate them to 64-bit integers or DATETIME. On the code side, compile 32-bit C with -D_TIME_BITS=64 (and -D_FILE_OFFSET_BITS=64, which it requires), and treat any remaining int cast of a time value as a bug.',
          'For hardware you do not control, the question becomes procurement: ask vendors whether the device stores time in 64 bits, and assume anything embedded and unpatchable that is still deployed will misbehave.',
        ],
      },
    ],
    faq: [
      { q: 'What is the year 2038 problem?', a: 'Signed 32-bit Unix timestamps overflow at 03:14:07 UTC on 19 January 2038 and wrap to 13 December 1901. Any system still storing time in a signed 32-bit field will jump 137 years into the past at that moment.' },
      { q: 'What is the exact date and time of the 2038 rollover?', a: '03:14:07 UTC on Tuesday 19 January 2038 — the moment the count of seconds since 1 January 1970 exceeds 2,147,483,647, the largest signed 32-bit integer.' },
      { q: 'Are 64-bit systems affected by the 2038 problem?', a: 'The platform itself is not: a signed 64-bit second count lasts about 292 billion years. But application schemas and file formats that still serialise 32-bit timestamps are affected even on 64-bit hardware.' },
      { q: 'Is MySQL affected by 2038?', a: 'The TIMESTAMP column type is: it stores 32-bit epoch seconds and ends at 2038-01-19 03:14:07 UTC. Migrating those columns to DATETIME, which reaches the year 9999, or to a 64-bit integer, resolves it.' },
      { q: 'Will the 2038 problem be fixed in time?', a: 'Mainstream operating systems, languages and file systems are already fixed. The residual risk is embedded devices without updates, unmigrated database columns, and old formats that hard-code a 32-bit timestamp.' },
    ],
  },
  {
    path: '/unix-time-in/python',
    view: 'REFERENCE',
    title: 'Unix Time in Python: time.time(), datetime and Conversions | EpochTimer',
    description:
      'How to get, convert and format Unix timestamps in Python: time.time(), datetime.fromtimestamp with timezones, milliseconds, the utcfromtimestamp deprecation, and the pitfalls.',
    heading: 'Unix Time in Python',
    priority: 0.7,
    body: [
      'Python exposes epoch time in two layers: the time module returns the raw number, and the datetime module converts between that number and calendar dates. Most bugs come from mixing the two carelessly — specifically from naive datetimes that silently assume local time.',
      'The current Unix timestamp is time.time(). It returns a float with sub-second precision; wrap it in int() for whole seconds, or multiply by 1000 and truncate for milliseconds.',
    ],
    sections: [
      {
        h2: 'Getting the current timestamp',
        list: [
          'import time; time.time() — seconds as a float, e.g. 1774137600.482.',
          'int(time.time()) — whole seconds, the form APIs and JWTs usually want.',
          'time.time_ns() — nanoseconds as an integer, immune to float rounding.',
          'From an aware datetime: datetime.now(timezone.utc).timestamp() gives the same number via the calendar layer.',
        ],
      },
      {
        h2: 'Timestamp to date and back',
        paras: [
          'Always attach a timezone. datetime.fromtimestamp(ts, tz=timezone.utc) returns an aware UTC datetime; passing no tz gives you the machine\u2019s local time with no marker saying so, which is how the same timestamp prints differently on a laptop and a server.',
          'The reverse direction is datetime(2038, 1, 19, 3, 14, 7, tzinfo=timezone.utc).timestamp(). On a naive datetime, .timestamp() interprets the value as local time — the single most common source of off-by-hours bugs in Python time handling.',
        ],
      },
      {
        h2: 'The utcnow and utcfromtimestamp deprecation',
        paras: [
          'datetime.utcnow() and datetime.utcfromtimestamp() are deprecated since Python 3.12, because they return naive datetimes that claim to be UTC but carry no tzinfo. The replacements are datetime.now(timezone.utc) and datetime.fromtimestamp(ts, tz=timezone.utc). Migrating is usually a find-and-replace, and it removes a whole class of ambiguity.',
        ],
      },
      {
        h2: 'Milliseconds, strings and pandas',
        list: [
          'JavaScript-style millisecond timestamps: divide by 1000 before fromtimestamp, or multiply a Python timestamp by 1000 before sending. Thirteen digits means milliseconds.',
          'Formatting: aware_dt.strftime(\'%Y-%m-%d %H:%M:%S\') for display, aware_dt.isoformat() for machine-readable output.',
          'Parsing ISO strings: datetime.fromisoformat() handles offsets like +00:00, and since Python 3.11 also a trailing Z.',
          'pandas: pd.to_datetime(series, unit=\'s\', utc=True) converts whole columns of epoch seconds; unit=\'ms\' for milliseconds.',
        ],
      },
    ],
    faq: [
      { q: 'How do I get the current Unix timestamp in Python?', a: 'Call time.time() for seconds as a float, or int(time.time()) for whole seconds. time.time_ns() returns nanoseconds as an integer.' },
      { q: 'How do I convert a Unix timestamp to a datetime in Python?', a: 'Use datetime.fromtimestamp(ts, tz=timezone.utc). Always pass a timezone — without one Python uses the machine\u2019s local time and returns a naive datetime with no marker saying so.' },
      { q: 'Why is datetime.utcnow() deprecated?', a: 'Since Python 3.12, because it returns a naive datetime that represents UTC but carries no tzinfo, which breaks .timestamp() and comparisons. Use datetime.now(timezone.utc) instead.' },
      { q: 'How do I get milliseconds since epoch in Python?', a: 'int(time.time() * 1000), or time.time_ns() // 1_000_000 to avoid float rounding entirely.' },
      { q: 'How do I convert a datetime to a Unix timestamp in Python?', a: 'Call .timestamp() on an aware datetime. On naive datetimes it assumes local time, so attach tzinfo=timezone.utc first if the value is meant to be UTC.' },
    ],
  },
  {
    path: '/unix-time-in/javascript',
    view: 'REFERENCE',
    title: 'Unix Time in JavaScript: Date.now(), Conversions and Pitfalls | EpochTimer',
    description:
      'How to work with Unix timestamps in JavaScript: Date.now() milliseconds, converting to seconds, parsing 10- vs 13-digit timestamps, formatting with Intl, and the Temporal API.',
    heading: 'Unix Time in JavaScript',
    priority: 0.7,
    body: [
      'JavaScript counts epoch time in milliseconds, not seconds. Date.now() returns the current count — a thirteen-digit number for any modern date — and every Date object is a thin wrapper around one such number. Nearly every JavaScript timestamp bug is a missing or extra factor of 1000 at the boundary with systems that count in seconds.',
      'For Unix-convention seconds, divide and truncate: Math.floor(Date.now() / 1000).',
    ],
    sections: [
      {
        h2: 'The essential conversions',
        list: [
          'Current milliseconds: Date.now(). Current seconds: Math.floor(Date.now() / 1000).',
          'Seconds to Date: new Date(seconds * 1000). Milliseconds to Date: new Date(ms).',
          'Date to milliseconds: date.getTime() (or +date). Date to seconds: Math.floor(date.getTime() / 1000).',
          'ISO string to timestamp: Date.parse(\'2038-01-19T03:14:07Z\') returns milliseconds; new Date(string).getTime() is equivalent.',
        ],
      },
      {
        h2: 'Ten digits or thirteen: guarding the boundary',
        paras: [
          'APIs, JWTs and databases usually speak seconds; JavaScript speaks milliseconds. Feed a ten-digit seconds value straight into new Date() and every date lands in January 1970; multiply an already-millisecond value by 1000 and dates land tens of thousands of years out. When accepting timestamps from outside, check the magnitude — a value below about 100 billion is almost certainly seconds — and normalise once, at the edge, instead of sprinkling *1000 through the codebase.',
        ],
      },
      {
        h2: 'Display: locales and time zones without libraries',
        paras: [
          'date.toISOString() always prints UTC, which is what logs and APIs want. For human display, Intl.DateTimeFormat handles locale and timezone natively: new Intl.DateTimeFormat(\'en-GB\', { dateStyle: \'medium\', timeStyle: \'short\', timeZone: \'Europe/Istanbul\' }).format(date). This replaces most historical uses of moment.js.',
          'Note that the legacy Date parser is reliable only for full ISO 8601 strings; formats like \'03/04/2026\' parse differently across locales and engines and are best avoided entirely.',
        ],
      },
      {
        h2: 'Temporal: the modern API',
        paras: [
          'The Temporal proposal replaces Date with precise, immutable types: Temporal.Now.instant() for the current moment, Temporal.Instant.fromEpochMilliseconds(ms) for conversions, and epochMilliseconds to read the count back. It also distinguishes instants from zoned and plain dates, removing the ambiguity Date was built on. It is shipping in Firefox and is behind flags elsewhere; for portable code today, Date plus Intl remains the baseline.',
        ],
      },
    ],
    faq: [
      { q: 'How do I get the current Unix timestamp in JavaScript?', a: 'Date.now() returns milliseconds since the epoch. For Unix-convention seconds use Math.floor(Date.now() / 1000).' },
      { q: 'Is JavaScript epoch time in seconds or milliseconds?', a: 'Milliseconds — thirteen digits for modern dates. Unix-convention seconds are ten digits, so convert with *1000 or /1000 at the boundary.' },
      { q: 'How do I convert a Unix timestamp to a date in JavaScript?', a: 'new Date(seconds * 1000) for a seconds timestamp, or new Date(ms) if the value is already in milliseconds. A date landing in 1970 means the *1000 is missing.' },
      { q: 'How do I format a timestamp for a specific time zone in JavaScript?', a: 'Use Intl.DateTimeFormat with the timeZone option, e.g. new Intl.DateTimeFormat(\'en-GB\', { dateStyle: \'medium\', timeStyle: \'short\', timeZone: \'America/New_York\' }).format(new Date(ts * 1000)). No library needed.' },
      { q: 'What is the Temporal API?', a: 'The successor to Date: immutable, nanosecond-precision types that separate instants from zoned and plain dates. Temporal.Now.instant() and Temporal.Instant.fromEpochMilliseconds() cover the epoch conversions. It is not yet available in all browsers.' },
    ],
  },
  {
    path: '/legal',
    view: 'LEGAL',
    title: 'Legal Notice | EpochTimer',
    description: 'Legal notice and site information for EpochTimer.',
    priority: 0.3,
  },
  {
    path: '/privacy',
    view: 'PRIVACY',
    title: 'Privacy Policy | EpochTimer',
    description:
      'How EpochTimer handles your data. All conversions run client-side, so the values you enter never reach a server.',
    priority: 0.3,
  },
  {
    path: '/terms',
    view: 'TERMS',
    title: 'Terms of Service | EpochTimer',
    description: 'The terms that govern your use of EpochTimer.',
    priority: 0.3,
  },
  {
    path: '/cookies',
    view: 'COOKIES',
    title: 'Cookie Policy | EpochTimer',
    description: 'Which cookies EpochTimer sets and why.',
    priority: 0.3,
  },
];

export const CANONICAL_ORIGIN = 'https://epochtimer.com';

/** Every valid path, for the server's 404 handling. */
export const ROUTE_PATHS = ROUTES.map((r) => r.path);

/**
 * Strip a trailing slash so '/batch/' and '/batch' resolve to one route.
 * @param {string} pathname
 */
export function normalizePath(pathname) {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '') || '/';
}

/**
 * @param {string} pathname
 * @returns {RouteDef | undefined}
 */
export function findRoute(pathname) {
  const normalized = normalizePath(pathname);
  return ROUTES.find((r) => r.path === normalized);
}
